import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import api from '../../services/api';
import { DEPTS } from './office-config';
import { OfficeTask } from './useOfficeEngine';

/* ============================================================
   语音播报中枢（与小灵同一 TTS 链路：POST /ai-demand-center/tts/speak）
   规则（用户定案）：
   · 播报必须自报家门（部门名开头）
   · 多任务严禁并发出声 —— 单通道排队，一条播完再播下一条
   · 排队超过 1 分钟的历史消息直接丢弃
   · 交办即播"收到任务马上安排"；过程关键变化播报；完成播"请领导查收"
   · 有产物 2 分钟没点开 → 追加提醒一次（只一次）
   ============================================================ */

const STALE_MS = 60_000;        // 队列里超过 1 分钟的消息丢弃
const STEP_GAP_MS = 15_000;     // 同一任务过程播报最小间隔
const REMIND_MS = 120_000;      // 产物未查看提醒延时

const deptName = (id: string) => DEPTS.find((d) => d.id === id)?.name || '相关部门';

/** 技术腔 → 大白话（播报绝不说"知识库精排/向量召回"这种话） */
const plainSpeech = (raw: string) => {
  const x = raw.replace(/[（(].*?[)）]/g, '').replace(/[….]+$/, '').trim();
  if (/知识库|检索|召回|精排|向量|语料|embedding|rag/i.test(x)) {
    return /完成|完毕|结束/.test(x) ? '相关知识搜索完成' : '正在搜索相关知识';
  }
  if (/提示词|prompt|大纲/i.test(x)) return '正在生成PPT内容';
  if (/思考|推理|research/i.test(x)) return '正在进行分析';
  if (/调阅资料|接单/.test(x)) return '已经接单，正在调阅资料';
  return x;
};

const base64ToArrayBuffer = (b64: string) => {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};

export function useVoiceHerald(tasks: OfficeTask[], enabled: boolean) {
  const queueRef = useRef<Array<{ text: string; ts: number }>>([]);
  const playingRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const seenRef = useRef<Set<string>>(new Set());
  const stepRef = useRef<Map<string, { text: string; at: number }>>(new Map());
  const doneRef = useRef<Set<string>>(new Set());
  const viewedRef = useRef<Set<string>>(new Set());
  const remindedRef = useRef<Set<string>>(new Set());
  const lastWarnAtRef = useRef(0);

  /* 单通道顺序播放：一条播完才取下一条；过期的直接丢 */
  const pump = useCallback(async () => {
    if (playingRef.current) return;
    playingRef.current = true;
    try {
      while (queueRef.current.length) {
        const item = queueRef.current.shift()!;
        if (Date.now() - item.ts > STALE_MS) continue;
        if (!enabledRef.current) continue;
        try {
          const { data } = await api.post('/ai-demand-center/tts/speak', {
            text: item.text, speed: 38, volume: 80, pitch: 55,
          });
          if (!data?.audioBase64) continue;
          /* 主路径=WebAudio 解码播放（与小灵一致，data-URI Audio 直放 mpeg 有起始杂音） */
          let played = false;
          try {
            if (!audioCtxRef.current) {
              const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
              if (Ctx) audioCtxRef.current = new Ctx();
            }
            const ctx = audioCtxRef.current;
            if (ctx) {
              if (ctx.state === 'suspended') await ctx.resume();
              const buffer = await ctx.decodeAudioData(base64ToArrayBuffer(data.audioBase64));
              // eslint-disable-next-line no-await-in-loop
              await new Promise<void>((resolve) => {
                const source = ctx.createBufferSource();
                source.buffer = buffer;
                source.connect(ctx.destination);
                source.onended = () => resolve();
                source.start(0);
              });
              played = true;
            }
          } catch { /* 解码失败走后备 */ }
          if (!played) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise<void>((resolve) => {
              const audio = new Audio(`data:${data.mime || 'audio/mpeg'};base64,${data.audioBase64}`);
              audio.preload = 'auto';
              audio.onended = () => resolve();
              audio.onerror = () => resolve();
              audio.play().catch(() => resolve());
            });
          }
        } catch (e: any) {
          /* 单条失败不阻塞队列；但语音服务挂了就"安静得像没坏"，必须让用户知道（限频 60s 一条） */
          const now = Date.now();
          if (now - lastWarnAtRef.current > 60_000) {
            lastWarnAtRef.current = now;
            const serverMsg = e?.response?.data?.message;
            message.warning(`语音播报失败：${typeof serverMsg === 'string' && serverMsg ? serverMsg : '语音合成服务异常，请到「系统设置 → 语音合成配置」检查'}`);
          }
        }
      }
    } finally {
      playingRef.current = false;
    }
  }, []);

  const speak = useCallback((text: string) => {
    if (!enabledRef.current || !text) return;
    queueRef.current.push({ text, ts: Date.now() });
    pump();
  }, [pump]);

  /** 成果被点开 → 取消"未查收"提醒 */
  const markViewed = useCallback((localId: string) => {
    viewedRef.current.add(localId);
  }, []);

  useEffect(() => {
    for (const t of tasks) {
      const dept = deptName(t.deptId);

      /* 新任务（会话恢复的历史任务不播） */
      if (!seenRef.current.has(t.localId)) {
        seenRef.current.add(t.localId);
        stepRef.current.set(t.localId, { text: t.stepText, at: Date.now() });
        if (!t.localId.startsWith('srv-')) speak(`${dept}收到任务，马上安排。`);
        continue;
      }

      /* 过程变化：同任务限频，慢任务（PPT/生图）也持续有声 */
      if (t.status === 'running' || t.status === 'dispatching') {
        const prev = stepRef.current.get(t.localId);
        if (t.stepText && prev && t.stepText !== prev.text && Date.now() - prev.at > STEP_GAP_MS) {
          stepRef.current.set(t.localId, { text: t.stepText, at: Date.now() });
          speak(`${dept}：${plainSpeech(t.stepText)}。`);
        }
      }

      /* 完成/失败 */
      if ((t.status === 'completed' || t.status === 'failed') && !doneRef.current.has(t.localId)) {
        doneRef.current.add(t.localId);
        if (t.localId.startsWith('srv-')) continue;
        if (t.status === 'completed') {
          speak(`${dept}：任务已完成，请领导查收。`);
          const id = t.localId;
          const hasArtifact = !!(t.resultFileId || t.resultImageUrl || t.pptProjectId || t.result);
          if (hasArtifact) {
            setTimeout(() => {
              if (!viewedRef.current.has(id) && !remindedRef.current.has(id)) {
                remindedRef.current.add(id);
                speak(`${dept}提醒：刚才完成的成果您还没有查看，请记得查收。`);
              }
            }, REMIND_MS);
          }
        } else {
          speak(`${dept}：任务遇到问题，请您过目处理。`);
        }
      }
    }
  }, [tasks, speak]);

  return { markViewed };
}
